/**
 * Standalone Meeting Join Page - Complete UI Overhaul
 * Modern video conferencing interface with clear controls
 */

import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useWebRTC } from '../../hooks/useWebRTC';
import { API_BASE } from '../../config/api';
import './MeetJoinPage.css';

interface Meeting {
  id: string;
  room_code: string;
  title: string;
  meeting_type: 'video' | 'voice';
  status: 'scheduled' | 'live' | 'ended';
  host_id?: string;
  host_name?: string;
}

interface Participant {
  id: string;
  odUserId: string;
  name: string;
  role: string;
  isMuted: boolean;
  hasVideo: boolean;
}

interface ChatMessage {
  id: string;
  userId: string;
  userName: string;
  message: string;
  timestamp: number;
}

// Persistent guest session - uses localStorage per meeting for rejoin capability
// IMPORTANT: Also checks for demo_user_id to maintain host identity across pages
const getGuestSession = (roomCode: string) => {
  const storageKey = `meeting_guest_${roomCode}`;

  // First check if user has a demo_user_id (from MeetingsPage) - use that for consistency
  const demoUserId = localStorage.getItem('demo_user_id');
  const demoUserName = localStorage.getItem('demo_user_name') || '';

  let session = localStorage.getItem(storageKey);

  if (session) {
    try {
      const parsed = JSON.parse(session);
      // If demo_user_id exists and differs from saved session, update to use demo_user_id
      // This ensures meeting creators are recognized as hosts when joining via public URL
      if (demoUserId && parsed.odUserId !== demoUserId) {
        const updatedSession = { ...parsed, odUserId: demoUserId };
        localStorage.setItem(storageKey, JSON.stringify(updatedSession));
        return updatedSession;
      }
      return parsed;
    } catch (e) {
      localStorage.removeItem(storageKey);
    }
  }

  // Create new session - prefer demo_user_id if available
  const newSession = {
    odUserId: demoUserId || ('guest_' + Math.random().toString(36).substring(2, 10)),
    name: demoUserName,
    createdAt: Date.now()
  };
  localStorage.setItem(storageKey, JSON.stringify(newSession));
  return newSession;
};

const saveGuestSession = (roomCode: string, odUserId: string, name: string) => {
  const storageKey = `meeting_guest_${roomCode}`;
  localStorage.setItem(storageKey, JSON.stringify({ odUserId, name, createdAt: Date.now() }));
};

export default function MeetJoinPage() {
  const { roomCode } = useParams<{ roomCode: string }>();
  const [stage, setStage] = useState<'loading' | 'preview' | 'joining' | 'meeting' | 'ended' | 'error'>('loading');
  const [meeting, setMeeting] = useState<Meeting | null>(null);
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [guestName, setGuestName] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  // UI State
  const [showChat, setShowChat] = useState(false);
  const [showParticipants, setShowParticipants] = useState(false);
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [messageInput, setMessageInput] = useState('');
  const [lastChatTime, setLastChatTime] = useState(0);
  const [pinnedUser, setPinnedUser] = useState<string | null>(null);

  const localVideoRef = useRef<HTMLVideoElement>(null);
  const previewVideoRef = useRef<HTMLVideoElement>(null);

  // Get persistent guest session for this specific meeting
  const guestSession = getGuestSession(roomCode || '');
  const guestId = guestSession.odUserId;

  // Check if user is admin - either by host_id match OR by having "host" role in participants
  const myParticipant = participants.find(p => p.odUserId === guestId);
  const isAdmin = meeting?.host_id === guestId || myParticipant?.role === 'host';

  // Pre-fill name from saved session
  useEffect(() => {
    if (guestSession.name && !guestName) {
      setGuestName(guestSession.name);
    }
  }, [guestSession.name]);

  const {
    localStream,
    remoteStreams,
    isAudioEnabled,
    isVideoEnabled,
    connectionState,
    startMedia,
    toggleAudio,
    toggleVideo,
    startScreenShare,
    stopScreenShare,
    isScreenSharing,
    startSignalPolling,
    disconnectAll,
    connectToPeer,
  } = useWebRTC(
    roomCode || '',
    guestId,
    (odUserId, stream) => console.log('[Meet] Remote stream from', odUserId),
    (odUserId) => setParticipants(prev => prev.filter(p => p.odUserId !== odUserId))
  );

  // Load meeting on mount
  useEffect(() => {
    if (!roomCode) {
      setErrorMessage('No room code provided');
      setStage('error');
      return;
    }

    const loadMeeting = async () => {
      try {
        const res = await fetch(`${API_BASE}/meetings/${roomCode}`);
        if (!res.ok) {
          setErrorMessage('Meeting not found');
          setStage('error');
          return;
        }
        const data = await res.json();
        setMeeting(data);
        if (data.status === 'ended') {
          setStage('ended');
        } else {
          setStage('preview');
          // Try preview camera
          try {
            const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
            if (previewVideoRef.current) previewVideoRef.current.srcObject = stream;
          } catch (e) {
            console.log('Preview camera not available');
          }
        }
      } catch (err) {
        setErrorMessage('Failed to load meeting');
        setStage('error');
      }
    };
    loadMeeting();

    return () => {
      if (previewVideoRef.current?.srcObject) {
        (previewVideoRef.current.srcObject as MediaStream).getTracks().forEach(t => t.stop());
      }
    };
  }, [roomCode]);

  // Attach local stream with retry and autoplay handling
  useEffect(() => {
    const videoEl = localVideoRef.current;
    if (!videoEl || !localStream) return;

    // Always set srcObject
    if (videoEl.srcObject !== localStream) {
      videoEl.srcObject = localStream;
    }

    // Ensure video plays (handle autoplay restrictions)
    const playVideo = async () => {
      try {
        await videoEl.play();
      } catch (err) {
        console.warn('[Video] Autoplay blocked, waiting for interaction');
        // Add click listener to start playback
        const onClick = async () => {
          try {
            await videoEl.play();
            document.removeEventListener('click', onClick);
          } catch (e) {}
        };
        document.addEventListener('click', onClick);
      }
    };

    playVideo();

    // Re-attach on track changes
    localStream.onaddtrack = () => {
      if (videoEl.srcObject !== localStream) {
        videoEl.srcObject = localStream;
        playVideo();
      }
    };

    return () => {
      localStream.onaddtrack = null;
    };
  }, [localStream]);

  // Poll participants
  useEffect(() => {
    if (stage !== 'meeting') return;
    const poll = async () => {
      try {
        const res = await fetch(`${API_BASE}/meetings/${roomCode}/participants`);
        const data = await res.json();
        setParticipants(data.participants || []);
      } catch (e) {}
    };
    poll();
    const interval = setInterval(poll, 3000);
    return () => clearInterval(interval);
  }, [stage, roomCode]);

  // Poll chat
  useEffect(() => {
    if (stage !== 'meeting') return;
    const poll = async () => {
      try {
        const res = await fetch(`${API_BASE}/meetings/${roomCode}/chat?since=${lastChatTime}`);
        const data = await res.json();
        if (data.messages?.length > 0) {
          setChatMessages(prev => {
            const ids = new Set(prev.map(m => m.id));
            return [...prev, ...data.messages.filter((m: ChatMessage) => !ids.has(m.id))];
          });
          setLastChatTime(Math.max(...data.messages.map((m: ChatMessage) => m.timestamp)));
        }
      } catch (e) {}
    };
    poll();
    const interval = setInterval(poll, 2000);
    return () => clearInterval(interval);
  }, [stage, roomCode, lastChatTime]);

  // Join meeting
  const joinMeeting = async () => {
    if (!guestName.trim()) return;
    setStage('joining');

    // Save guest session with name for future rejoins
    saveGuestSession(roomCode || '', guestId, guestName);

    // Stop preview
    if (previewVideoRef.current?.srcObject) {
      (previewVideoRef.current.srcObject as MediaStream).getTracks().forEach(t => t.stop());
    }

    try {
      // Pass userId (guestId) so backend can detect rejoin
      await fetch(`${API_BASE}/meetings/${roomCode}/join`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: guestId, guestName, guestEmail: null }),
      });

      try {
        await startMedia(meeting?.meeting_type || 'video');
      } catch (e) {
        console.warn('No camera/mic available');
      }

      startSignalPolling();

      const pRes = await fetch(`${API_BASE}/meetings/${roomCode}/participants`);
      const pData = await pRes.json();
      setParticipants(pData.participants || []);

      for (const p of pData.participants || []) {
        if (p.odUserId !== guestId) await connectToPeer(p.odUserId);
      }

      setStage('meeting');
    } catch (err) {
      setErrorMessage('Failed to join meeting');
      setStage('error');
    }
  };

  // Leave meeting
  const leaveMeeting = async () => {
    try {
      await fetch(`${API_BASE}/meetings/${roomCode}/leave`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ odUserId: guestId }),
      });
    } catch (e) {}
    disconnectAll();
    setStage('ended');
  };

  // Restart camera - useful when video doesn't show
  const restartCamera = async () => {
    try {
      // Stop existing stream
      if (localStream) {
        localStream.getTracks().forEach(track => track.stop());
      }
      // Start fresh
      await startMedia(meeting?.meeting_type || 'video');
      console.log('[Meet] Camera restarted');
    } catch (err) {
      console.error('[Meet] Failed to restart camera:', err);
    }
  };

  // End meeting (admin)
  const endMeeting = async () => {
    if (!isAdmin) return;
    try {
      await fetch(`${API_BASE}/meetings/${roomCode}/end`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ hostId: guestId }),
      });
    } catch (e) {}
    disconnectAll();
    setStage('ended');
  };

  // Kick participant (admin)
  const kickParticipant = async (participantId: string) => {
    if (!isAdmin) return;
    try {
      await fetch(`${API_BASE}/meetings/${roomCode}/kick`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ hostId: guestId, participantId }),
      });
      setParticipants(prev => prev.filter(p => p.odUserId !== participantId));
    } catch (e) {}
  };

  // Send chat
  const sendChat = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!messageInput.trim()) return;
    try {
      const res = await fetch(`${API_BASE}/meetings/${roomCode}/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: guestId, userName: guestName, message: messageInput }),
      });
      const data = await res.json();
      setChatMessages(prev => [...prev, data]);
      setMessageInput('');
    } catch (e) {}
  };

  // Copy link
  const copyLink = () => {
    navigator.clipboard.writeText(window.location.href);
  };

  // ============ RENDER ============

  // Loading
  if (stage === 'loading') {
    return (
      <div className="meet-container meet-container--centered">
        <div className="meet-loader">
          <div className="meet-spinner"></div>
          <p>Loading meeting...</p>
        </div>
      </div>
    );
  }

  // Error
  if (stage === 'error') {
    return (
      <div className="meet-container meet-container--centered">
        <div className="meet-error-card">
          <div className="meet-error-icon">!</div>
          <h1>Unable to Join</h1>
          <p>{errorMessage}</p>
          <button onClick={() => window.location.href = '/'} className="meet-btn meet-btn--primary">
            Go Home
          </button>
        </div>
      </div>
    );
  }

  // Ended
  if (stage === 'ended') {
    return (
      <div className="meet-container meet-container--centered">
        <div className="meet-ended-card">
          <div className="meet-ended-icon">👋</div>
          <h1>Meeting Ended</h1>
          <p>Thanks for joining!</p>
          <button onClick={() => window.location.href = '/'} className="meet-btn meet-btn--primary">
            Go Home
          </button>
        </div>
      </div>
    );
  }

  // Preview
  if (stage === 'preview' || stage === 'joining') {
    return (
      <div className="meet-container meet-container--centered">
        <div className="meet-preview-card">
          <h1>{meeting?.title || 'Video Meeting'}</h1>
          <p className="meet-room-code">Room: {roomCode}</p>

          <div className="meet-preview-video">
            {meeting?.meeting_type === 'video' ? (
              <video ref={previewVideoRef} autoPlay muted playsInline />
            ) : (
              <div className="meet-preview-audio">
                <span>🎙️</span>
                <p>Voice Call</p>
              </div>
            )}
          </div>

          <input
            type="text"
            placeholder="Enter your name"
            value={guestName}
            onChange={e => setGuestName(e.target.value)}
            className="meet-name-input"
            disabled={stage === 'joining'}
            onKeyDown={e => e.key === 'Enter' && joinMeeting()}
          />

          <button
            onClick={joinMeeting}
            disabled={!guestName.trim() || stage === 'joining'}
            className="meet-btn meet-btn--primary meet-btn--large"
          >
            {stage === 'joining' ? 'Joining...' : 'Join Meeting'}
          </button>

          <div className="meet-preview-info">
            <span>🔒 End-to-end encrypted</span>
            {meeting?.host_name && <span>👤 Host: {meeting.host_name}</span>}
          </div>
        </div>
      </div>
    );
  }

  // ============ MEETING VIEW ============
  const otherParticipants = participants.filter(p => p.odUserId !== guestId);
  // Count ALL video tiles: local + all other participants (connected or not)
  const totalVideos = 1 + Math.max(otherParticipants.length, remoteStreams.size);
  // Determine grid layout class based on participant count
  const gridClass = totalVideos <= 1 ? 1 : totalVideos <= 2 ? 2 : totalVideos <= 4 ? 4 : totalVideos <= 6 ? 6 : 9;

  return (
    <div className="meet-container">
      {/* HEADER */}
      <header className="meet-header">
        <div className="meet-header-left">
          <h1>{meeting?.title || 'Meeting'}</h1>
          <span className="meet-live-badge">LIVE</span>
          {connectionState === 'connecting' && (
            <span className="meet-connection-badge meet-connection-badge--connecting">⏳ Connecting</span>
          )}
          {connectionState === 'connected' && remoteStreams.size > 0 && (
            <span className="meet-connection-badge meet-connection-badge--connected">✓ {remoteStreams.size} connected</span>
          )}
          <span className="meet-room-id">{roomCode}</span>
        </div>
        <div className="meet-header-right">
          <button className="meet-header-btn" onClick={copyLink} title="Copy Link">
            🔗
          </button>
          <button
            className={`meet-header-btn ${showParticipants ? 'active' : ''}`}
            onClick={() => setShowParticipants(!showParticipants)}
            title="Participants"
          >
            👥 <span className="meet-badge">{participants.length}</span>
          </button>
          <button
            className={`meet-header-btn ${showChat ? 'active' : ''}`}
            onClick={() => setShowChat(!showChat)}
            title="Chat"
          >
            💬
          </button>
          {isAdmin && (
            <button className="meet-header-btn meet-header-btn--danger" onClick={endMeeting} title="End Meeting">
              End
            </button>
          )}
        </div>
      </header>

      {/* MAIN CONTENT */}
      <main className={`meet-main ${showChat || showParticipants ? 'meet-main--with-sidebar' : ''}`}>
        {/* VIDEO GRID */}
        <div className={`meet-video-grid meet-video-grid--${gridClass}`}>
          {/* Local Video */}
          <div
            className={`meet-video-tile meet-video-tile--local ${pinnedUser === guestId ? 'meet-video-tile--pinned' : ''}`}
            onClick={() => setPinnedUser(pinnedUser === guestId ? null : guestId)}
          >
            {/* Always render video element for consistent ref attachment */}
            <video
              ref={localVideoRef}
              autoPlay
              muted
              playsInline
              style={{ display: localStream && isVideoEnabled ? 'block' : 'none' }}
            />
            {/* Show avatar when no video */}
            {(!localStream || !isVideoEnabled) && (
              <div className="meet-video-avatar">
                <span>{guestName?.[0]?.toUpperCase() || '?'}</span>
              </div>
            )}
            <div className="meet-video-label">
              <span className="meet-video-name">
                {guestName} (You) {isAdmin && '👑'}
              </span>
              {!isAudioEnabled && <span className="meet-muted">🔇</span>}
              {!localStream && <span className="meet-no-camera">📷 No camera</span>}
            </div>
          </div>

          {/* Remote Videos */}
          {Array.from(remoteStreams.entries()).map(([odUserId, stream]) => {
            const p = participants.find(x => x.odUserId === odUserId);
            return (
              <div
                key={odUserId}
                className={`meet-video-tile ${pinnedUser === odUserId ? 'meet-video-tile--pinned' : ''}`}
                onClick={() => setPinnedUser(pinnedUser === odUserId ? null : odUserId)}
              >
                <video
                  autoPlay
                  playsInline
                  ref={el => { if (el && el.srcObject !== stream) el.srcObject = stream; }}
                />
                <div className="meet-video-label">
                  <span className="meet-video-name">
                    {p?.name || 'Guest'} {p?.role === 'host' && '👑'}
                  </span>
                  {p?.isMuted && <span className="meet-muted">🔇</span>}
                </div>
                {isAdmin && (
                  <button
                    className="meet-video-kick"
                    onClick={(e) => { e.stopPropagation(); kickParticipant(odUserId); }}
                    title="Remove"
                  >
                    ✕
                  </button>
                )}
              </div>
            );
          })}

          {/* Connecting placeholders */}
          {otherParticipants
            .filter(p => !remoteStreams.has(p.odUserId))
            .map(p => (
              <div key={p.odUserId} className="meet-video-tile meet-video-tile--connecting">
                <div className="meet-video-avatar">
                  <span>{p.name?.[0]?.toUpperCase() || '?'}</span>
                </div>
                <div className="meet-video-label">
                  <span className="meet-video-name">{p.name}</span>
                  <span className="meet-connecting">Connecting...</span>
                </div>
                <button
                  className="meet-video-reconnect"
                  onClick={(e) => { e.stopPropagation(); connectToPeer(p.odUserId); }}
                  title="Retry Connection"
                >
                  🔄
                </button>
                {isAdmin && (
                  <button
                    className="meet-video-kick"
                    onClick={() => kickParticipant(p.odUserId)}
                    title="Remove"
                  >
                    ✕
                  </button>
                )}
              </div>
            ))}

          {/* Empty state */}
          {otherParticipants.length === 0 && remoteStreams.size === 0 && (
            <div className="meet-video-tile meet-video-tile--empty">
              <div className="meet-waiting">
                <span>⏳</span>
                <p>Waiting for others...</p>
                <button className="meet-btn meet-btn--small" onClick={copyLink}>
                  Copy Invite Link
                </button>
              </div>
            </div>
          )}
        </div>

        {/* SIDEBAR */}
        {(showChat || showParticipants) && (
          <aside className="meet-sidebar">
            {/* Tabs */}
            <div className="meet-sidebar-tabs">
              <button
                className={showParticipants ? 'active' : ''}
                onClick={() => { setShowParticipants(true); setShowChat(false); }}
              >
                Participants ({participants.length})
              </button>
              <button
                className={showChat ? 'active' : ''}
                onClick={() => { setShowChat(true); setShowParticipants(false); }}
              >
                Chat
              </button>
              <button className="meet-sidebar-close" onClick={() => { setShowChat(false); setShowParticipants(false); }}>
                ✕
              </button>
            </div>

            {/* Participants Panel */}
            {showParticipants && (
              <div className="meet-participants-list">
                {participants.map(p => (
                  <div key={p.odUserId} className="meet-participant-item">
                    <div className="meet-participant-avatar">{p.name?.[0]?.toUpperCase()}</div>
                    <div className="meet-participant-info">
                      <span className="meet-participant-name">
                        {p.name} {p.odUserId === guestId && '(You)'} {p.role === 'host' && '👑'}
                      </span>
                      <span className="meet-participant-status">
                        {p.isMuted ? '🔇 Muted' : '🎤 Active'}
                      </span>
                    </div>
                    {isAdmin && p.odUserId !== guestId && (
                      <button className="meet-kick-btn" onClick={() => kickParticipant(p.odUserId)}>
                        Remove
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Chat Panel */}
            {showChat && (
              <div className="meet-chat-panel">
                <div className="meet-chat-messages">
                  {chatMessages.length === 0 ? (
                    <p className="meet-chat-empty">No messages yet</p>
                  ) : (
                    chatMessages.map(m => (
                      <div key={m.id} className={`meet-chat-msg ${m.userId === guestId ? 'meet-chat-msg--own' : ''}`}>
                        <span className="meet-chat-sender">{m.userId === guestId ? 'You' : m.userName}</span>
                        <span className="meet-chat-text">{m.message}</span>
                        <span className="meet-chat-time">
                          {new Date(m.timestamp * 1000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </span>
                      </div>
                    ))
                  )}
                </div>
                <form className="meet-chat-input" onSubmit={sendChat}>
                  <input
                    type="text"
                    placeholder="Type a message..."
                    value={messageInput}
                    onChange={e => setMessageInput(e.target.value)}
                  />
                  <button type="submit">Send</button>
                </form>
              </div>
            )}
          </aside>
        )}
      </main>

      {/* CONTROLS */}
      <footer className="meet-controls">
        <button
          className={`meet-control-btn ${!isAudioEnabled ? 'meet-control-btn--off' : ''}`}
          onClick={toggleAudio}
          title={isAudioEnabled ? 'Mute' : 'Unmute'}
        >
          {isAudioEnabled ? '🎤' : '🔇'}
          <span>{isAudioEnabled ? 'Mute' : 'Unmute'}</span>
        </button>

        {meeting?.meeting_type === 'video' && (
          <>
            <button
              className={`meet-control-btn ${!isVideoEnabled ? 'meet-control-btn--off' : ''}`}
              onClick={toggleVideo}
              title={isVideoEnabled ? 'Stop Video' : 'Start Video'}
            >
              {isVideoEnabled ? '📹' : '📷'}
              <span>{isVideoEnabled ? 'Stop Video' : 'Start Video'}</span>
            </button>
            {!localStream && (
              <button
                className="meet-control-btn meet-control-btn--warning"
                onClick={restartCamera}
                title="Restart Camera"
              >
                🔄
                <span>Restart Cam</span>
              </button>
            )}
          </>
        )}

        <button
          className={`meet-control-btn ${isScreenSharing ? 'meet-control-btn--active' : ''}`}
          onClick={isScreenSharing ? stopScreenShare : startScreenShare}
          title={isScreenSharing ? 'Stop Share' : 'Share Screen'}
        >
          🖥️
          <span>{isScreenSharing ? 'Stop Share' : 'Share'}</span>
        </button>

        <button className="meet-control-btn meet-control-btn--end" onClick={leaveMeeting}>
          📞
          <span>Leave</span>
        </button>
      </footer>
    </div>
  );
}
